import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { CalendarRange } from "lucide-react";

/**
 * DateRangePicker — header-mounted control used by /pre-registrations.
 *
 * v2 B1 (PR-2C close-out follow-up). Scope is intentionally narrow:
 *   - 4 presets + custom range
 *   - The trigger / summary labels honour the active locale via `useLocale()`,
 *     so an org flipped to en-US will see MM/DD/YYYY here automatically.
 *   - Returns ISO strings via onChange so the caller can drop them into the
 *     existing `/api/pre-registrations?from=&to=` query unchanged
 */

// --- helpers (kept local — B2 will lift these to a global locale module) ---

function startOfDay(d) {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function endOfDay(d) {
  const dt = new Date(d);
  dt.setHours(23, 59, 59, 999);
  return dt;
}

function startOfWeekMon(d = new Date()) {
  const dt = startOfDay(d);
  const dow = dt.getDay() === 0 ? 6 : dt.getDay() - 1; // Monday-first
  dt.setDate(dt.getDate() - dow);
  return dt;
}

function addDays(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
}

export function fmtDateDMY(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// Format a Date as YYYY-MM-DD for <input type="date"> (browser ISO).
function isoDay(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export const PRESETS = [
  { key: "today",    label: "Today" },
  { key: "this_week", label: "This week" },
  { key: "next_7",   label: "Next 7 days" },
  { key: "next_30",  label: "Next 30 days" },
  { key: "custom",   label: "Custom range" },
];

export function rangeForPreset(preset, today = new Date()) {
  switch (preset) {
    case "today":
      return { from: startOfDay(today), to: endOfDay(today) };
    case "this_week": {
      const ws = startOfWeekMon(today);
      return { from: ws, to: endOfDay(addDays(ws, 6)) };
    }
    case "next_7":
      return { from: startOfDay(today), to: endOfDay(addDays(today, 6)) };
    case "next_30":
      return { from: startOfDay(today), to: endOfDay(addDays(today, 29)) };
    default:
      return null;
  }
}

export default function DateRangePicker({ value, onChange }) {
  // `value` shape: { preset: string, from: Date, to: Date }
  const { formatDate } = useLocale();   // Phase B2 — locale-aware labels.
  const [open, setOpen] = useState(false);
  // Local-state copies used while the popover is open so a half-typed custom
  // range doesn't fire the API on every keystroke.
  const [draftFrom, setDraftFrom] = useState(value?.from ?? new Date());
  const [draftTo, setDraftTo] = useState(value?.to ?? new Date());

  const label = useMemo(() => {
    if (!value?.from || !value?.to) return "Pick a range";
    if (value.preset === "today") return "Today";
    return `${formatDate(value.from)} – ${formatDate(value.to)}`;
  }, [value, formatDate]);

  const pickPreset = (preset) => {
    if (preset === "custom") {
      // Open the custom inputs but DON'T fire onChange until user types.
      onChange({ preset: "custom", from: draftFrom, to: draftTo });
      return;
    }
    const r = rangeForPreset(preset);
    if (!r) return;
    onChange({ preset, from: r.from, to: r.to });
    setOpen(false);
  };

  const applyCustom = () => {
    const f = startOfDay(draftFrom);
    const t = endOfDay(draftTo);
    if (t < f) {
      onChange({ preset: "custom", from: t, to: endOfDay(f) });
    } else {
      onChange({ preset: "custom", from: f, to: t });
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-9 gap-2 text-xs font-medium border-navy-200 text-navy-700 hover:bg-navy-50"
          data-testid="pre-regs-date-range-trigger"
        >
          <CalendarRange className="w-3.5 h-3.5" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3 space-y-3"
        data-testid="pre-regs-date-range-popover"
      >
        <div className="text-[11px] uppercase tracking-wider text-navy-400 font-semibold">
          Quick ranges
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => pickPreset(p.key)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                value?.preset === p.key
                  ? "bg-navy-900 text-white border-navy-900"
                  : "bg-white border-navy-200 text-navy-700 hover:bg-navy-50"
              }`}
              data-testid={`pre-regs-date-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {value?.preset === "custom" && (
          <div
            className="pt-2 border-t border-navy-100 space-y-2"
            data-testid="pre-regs-date-custom-panel"
          >
            <div className="text-[11px] uppercase tracking-wider text-navy-400 font-semibold">
              Custom range (DD/MM/YYYY)
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-navy-500 w-10">From</label>
              <input
                type="date"
                value={isoDay(draftFrom)}
                onChange={(e) => setDraftFrom(new Date(e.target.value))}
                className="text-xs border border-navy-200 rounded-md px-2 py-1 flex-1"
                data-testid="pre-regs-date-from-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-navy-500 w-10">To</label>
              <input
                type="date"
                value={isoDay(draftTo)}
                onChange={(e) => setDraftTo(new Date(e.target.value))}
                className="text-xs border border-navy-200 rounded-md px-2 py-1 flex-1"
                data-testid="pre-regs-date-to-input"
              />
            </div>
            <div className="text-[11px] text-navy-500" data-testid="pre-regs-date-custom-summary">
              {formatDate(draftFrom)} – {formatDate(draftTo)}
            </div>
            <Button
              onClick={applyCustom}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xs"
              data-testid="pre-regs-date-custom-apply"
            >
              Apply
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
